import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import type { RestaurantMealSpaceCandidate, RestaurantMealSpaceSelection } from '@/models/restaurant-manager'
import { formatMealSpaceTimeRangeLabel } from '@/models/restaurant-manager'
import { isSameSelection } from './use-restaurant-meal-space-draft'

type Props = {
  value: RestaurantMealSpaceSelection | null
  candidateSpaces: RestaurantMealSpaceCandidate[]
  isDropdownOpen: boolean
  isLoading: boolean
  disabled: boolean
  onOpenChange: (open: boolean) => void
  onChange: (selection: RestaurantMealSpaceSelection) => void
}

type SpaceGroup = {
  spaceId: string
  spaceName: string
  ranges: RestaurantMealSpaceSelection[]
}

type PopupPosition = {
  left: number
  width: number
  top?: number
  bottom?: number
}

const POPUP_MAX_HEIGHT = 360
const POPUP_MIN_WIDTH = 280
const POPUP_OFFSET = 4

export const RestaurantMealSpaceCandidateSelect: React.FC<Props> = ({
  value,
  candidateSpaces,
  isDropdownOpen,
  isLoading,
  disabled,
  onOpenChange,
  onChange,
}) => {
  const { t } = useTranslation()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const [expandedSpaceIds, setExpandedSpaceIds] = useState<string[]>([])
  const [popupPosition, setPopupPosition] = useState<PopupPosition | null>(null)

  const spaceGroups = useMemo(() => buildSpaceGroups(candidateSpaces, value), [candidateSpaces, value])

  const toggleExpanded = (spaceId: string) => {
    setExpandedSpaceIds(currentIds => (currentIds.includes(spaceId) ? currentIds.filter(id => id !== spaceId) : [...currentIds, spaceId]))
  }

  useEffect(() => {
    setExpandedSpaceIds(isDropdownOpen && value ? [value.spaceId] : [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDropdownOpen])

  const updatePopupPosition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) {
      return
    }

    const rect = trigger.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const shouldOpenUpward = spaceBelow < POPUP_OFFSET + POPUP_MAX_HEIGHT && rect.top > spaceBelow
    setPopupPosition({
      left: rect.left,
      width: Math.max(rect.width, POPUP_MIN_WIDTH),
      ...(shouldOpenUpward ? { bottom: window.innerHeight - rect.top + POPUP_OFFSET } : { top: rect.bottom + POPUP_OFFSET }),
    })
  }, [])

  useLayoutEffect(() => {
    if (!isDropdownOpen) {
      setPopupPosition(null)
      return
    }

    updatePopupPosition()
    window.addEventListener('resize', updatePopupPosition)
    document.addEventListener('scroll', updatePopupPosition, true)
    return () => {
      window.removeEventListener('resize', updatePopupPosition)
      document.removeEventListener('scroll', updatePopupPosition, true)
    }
  }, [isDropdownOpen, updatePopupPosition])

  useEffect(() => {
    if (!isDropdownOpen) {
      return
    }

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Node)) {
        return
      }
      if (triggerRef.current?.contains(target) || popupRef.current?.contains(target)) {
        return
      }
      onOpenChange(false)
    }

    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [isDropdownOpen, onOpenChange])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && isDropdownOpen) {
      event.stopPropagation()
      onOpenChange(false)
      triggerRef.current?.focus()
    }
  }

  return (
    <div css={wrapperStyle}>
      <button
        ref={triggerRef}
        type="button"
        css={triggerStyle(Boolean(value), isDropdownOpen)}
        aria-haspopup="listbox"
        aria-expanded={isDropdownOpen}
        disabled={disabled}
        onClick={() => onOpenChange(!isDropdownOpen)}
        onKeyDown={handleKeyDown}
      >
        {value ? (
          <span css={selectedLabelStyle}>
            <span css={selectedSpaceStyle}>{value.spaceName}</span>
            <span css={selectedTimeStyle}>・{formatMealSpaceTimeRangeLabel(value)}</span>
          </span>
        ) : (
          <span css={placeholderStyle}>{t('Please select')}</span>
        )}
        <img css={arrowStyle} src={require('@/static/images/arrow_down_yellow.svg')} alt="" />
      </button>

      {isDropdownOpen &&
        popupPosition &&
        createPortal(
          <div ref={popupRef} className="meal-space-select-popup" role="listbox" css={popupStyle(popupPosition)} onKeyDown={handleKeyDown}>
            {isLoading ? (
              <div css={popupMessageStyle}>{t('Loading')}</div>
            ) : spaceGroups.length === 0 ? (
              <div css={popupMessageStyle}>-</div>
            ) : (
              spaceGroups.map(group => {
                const isExpanded = expandedSpaceIds.includes(group.spaceId)
                const isEmpty = group.ranges.length === 0
                return (
                  <React.Fragment key={group.spaceId}>
                    <button type="button" css={groupHeaderStyle} aria-expanded={isExpanded} onClick={() => toggleExpanded(group.spaceId)}>
                      <span css={triangleStyle(isExpanded, isEmpty)}>▶</span>
                      <span css={groupNameStyle(isEmpty)}>{group.spaceName}</span>
                    </button>
                    {isExpanded &&
                      group.ranges.map(selection => {
                        const isSelected = value != null && isSameSelection(value, selection)
                        return (
                          <button
                            key={`${selection.useDateStart}__${selection.useDateEnd}`}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            css={optionStyle(isSelected)}
                            onClick={() => onChange(selection)}
                          >
                            {formatMealSpaceTimeRangeLabel(selection)}
                          </button>
                        )
                      })}
                  </React.Fragment>
                )
              })
            )}
          </div>,
          document.body,
        )}
    </div>
  )
}

const buildSpaceGroups = (
  candidateSpaces: RestaurantMealSpaceCandidate[],
  currentSelection: RestaurantMealSpaceSelection | null,
): SpaceGroup[] => {
  const groups = candidateSpaces.map(space => ({
    spaceId: space.spaceId,
    spaceName: space.spaceName,
    ranges: space.availableTimeRanges
      .slice()
      .sort((left, right) => left.useDateStart.localeCompare(right.useDateStart))
      .map(range => ({
        spaceId: space.spaceId,
        spaceName: space.spaceName,
        useDateStart: range.useDateStart,
        useDateEnd: range.useDateEnd,
      })),
  }))

  if (!currentSelection) {
    return groups
  }

  const hasCurrentSelection = groups.some(group => group.ranges.some(range => isSameSelection(range, currentSelection)))
  if (hasCurrentSelection) {
    return groups
  }

  const existingGroup = groups.find(group => group.spaceId === currentSelection.spaceId)
  if (existingGroup) {
    existingGroup.ranges = [...existingGroup.ranges, { ...currentSelection }]
  } else {
    groups.push({
      spaceId: currentSelection.spaceId,
      spaceName: currentSelection.spaceName,
      ranges: [{ ...currentSelection }],
    })
  }

  return groups
}

const wrapperStyle = css({
  flex: '0 0 92%',
  minWidth: 0,
  width: '92%',
})

const triggerStyle = (isActive: boolean, isOpen: boolean) =>
  css({
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    border: `1px solid ${isActive || isOpen ? '#F2A40B' : '#CCCCCC'}`,
    borderRadius: 18,
    color: '#272727',
    cursor: 'pointer',
    display: 'flex',
    fontSize: 14,
    minHeight: 36,
    minWidth: 0,
    padding: '0 32px 0 12px',
    position: 'relative',
    textAlign: 'left',
    width: '100%',
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  })

const selectedLabelStyle = css({
  alignItems: 'center',
  display: 'inline-flex',
  maxWidth: '100%',
  minWidth: 0,
  fontSize: 12,
})

const selectedSpaceStyle = css({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const selectedTimeStyle = css({
  flexShrink: 0,
  whiteSpace: 'nowrap',
})

const placeholderStyle = css({
  color: '#999999',
})

const arrowStyle = css({
  display: 'block',
  height: 7,
  position: 'absolute',
  right: 14,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 12,
})

const popupStyle = (position: PopupPosition) =>
  css({
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    boxShadow: '0px 3px 10px #0000003d',
    maxHeight: POPUP_MAX_HEIGHT,
    overflowY: 'auto',
    padding: '8px 0',
    position: 'fixed',
    zIndex: 1050,
    ...position,
  })

const popupMessageStyle = css({
  color: '#999999',
  fontSize: 14,
  padding: '6px 12px',
})

const groupHeaderStyle = css({
  alignItems: 'center',
  background: 'transparent',
  border: 'none',
  color: '#272727',
  cursor: 'pointer',
  display: 'flex',
  fontSize: 14,
  gap: 8,
  minHeight: 32,
  padding: '0 12px',
  textAlign: 'left',
  width: '100%',
  '&:hover': {
    backgroundColor: '#F5F5F5',
  },
})

const triangleStyle = (isExpanded: boolean, isEmpty: boolean) =>
  css({
    alignItems: 'center',
    display: 'inline-flex',
    flexShrink: 0,
    fontSize: 10,
    justifyContent: 'center',
    transform: isExpanded ? 'rotate(90deg)' : 'none',
    transition: 'transform 0.15s',
    width: 12,
    color: isEmpty ? '#999999' : '#272727',
  })

const groupNameStyle = (isEmpty: boolean) =>
  css({
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: isEmpty ? '#999999' : '#272727',
  })

const optionStyle = (isSelected: boolean) =>
  css({
    backgroundColor: isSelected ? '#FAFAFA' : 'transparent',
    border: 'none',
    color: isSelected ? '#F2A40B' : '#272727',
    cursor: 'pointer',
    display: 'block',
    fontSize: 14,
    minHeight: 32,
    padding: '6px 12px 6px 32px',
    textAlign: 'left',
    width: '100%',
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  })
